/* eslint-disable no-restricted-imports */

import { FocusTrap } from '@mui/base/FocusTrap';
import {
    BottomNavigationAction as OgBottomNavigationAction,
    BottomNavigationActionProps as OgBottomNavigationActionProps,
    Button as OgButton,
    ButtonBase as OgButtonBase,
    ButtonBaseProps as OgButtonBaseProps,
    ButtonProps as OgButtonProps,
    CardActionArea as OgCardActionArea,
    CardActionAreaProps as OgCardActionAreaProps,
    Chip as OgChip,
    ChipProps as OgChipProps,
    IconButton as OgIconButton,
    IconButtonProps as OgIconButtonProps,
    ListItemButton as OgListItemButton,
    ListItemButtonProps as OgListItemButtonProps,
    MenuItem as OgMenuItem,
    MenuItemProps as OgMenuItemProps,
    Tab as OgTab,
    TableCell,
    TableCellProps,
    SpeedDialAction as OgSpeedDialAction,
    SpeedDialActionProps as OgSpeedDialActionProps,
    Fab as OgFab,
    FabProps as OgFabProps,
    TabProps,
} from '@mui/material';
import {
    ButtonAnalyticsProps,
    ButtonWithoutAnalyticsProps,
    DialogAnalyticsProps,
    withButtonAnalytics,
    withDialogAnalytics,
} from '@rhg/libs/analytics/with-analytics';
import {
    Dialog as OgDialog,
    DialogProps as OgDialogProps,
} from './overrides/dialog';

export {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    alpha,
    AppBar,
    Backdrop,
    Box,
    Card,
    CardHeader,
    Checkbox,
    CircularProgress,
    ClickAwayListener,
    Container,
    CssBaseline,
    DialogActions,
    Divider,
    Drawer,
    Fade,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    Grow,
    Icon,
    Input,
    InputAdornment,
    InputLabel,
    Link,
    List,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Modal,
    Paper,
    Popper,
    Radio,
    Select,
    Skeleton,
    Slide,
    Slider,
    Snackbar,
    SpeedDial,
    Stack,
    Step,
    StepConnector,
    StepIcon,
    StepLabel,
    Stepper,
    SvgIcon,
    Switch,
    Tabs,
    TextField,
    ThemeProvider,
    Toolbar,
    Tooltip,
    tooltipClasses,
    Typography,
    styled,
    useMediaQuery,
    useTheme,
} from '@mui/material';
export type {
    AlertColor,
    Breakpoint,
    CheckboxProps,
    ContainerProps,
    DialogProps,
    FadeProps,
    FormControlProps,
    GridProps,
    InputBaseComponentProps,
    ListItemTextProps,
    PaletteMode,
    PaletteOptions,
    RadioProps,
    SkeletonProps,
    SvgIconProps,
    SxProps,
    TextFieldProps,
    Theme,
    TooltipProps,
    TypographyProps,
} from '@mui/material';
export { FocusTrap };

// Custom components overriding existing ones
export { Avatar } from './overrides/Avatar';
export type { AvatarProps } from './overrides/Avatar';
export { LinearProgress } from './overrides/linear-progress';
export type { LinearProgressProps } from './overrides/linear-progress';
export { Menu, type MenuProps } from './overrides/menu';

// Helper type for components that support both analytics and skipTracking
export type ButtonWithOptionalAnalytics<T> =
    | ButtonAnalyticsProps<T>
    | ButtonWithoutAnalyticsProps<T>;

export type BottomNavigationActionProps =
    ButtonAnalyticsProps<OgBottomNavigationActionProps>;
export type ButtonProps = ButtonAnalyticsProps<
    OgButtonProps & { size?: 'large' | 'small' }
>;
export type ButtonBaseProps = ButtonAnalyticsProps<
    OgButtonBaseProps & { size?: 'large' | 'small' }
>;
export type ChipProps = ButtonAnalyticsProps<OgChipProps>;
export type IconButtonProps = ButtonAnalyticsProps<OgIconButtonProps>;
export type ListItemButtonProps = ButtonAnalyticsProps<OgListItemButtonProps>;
export type MenuItemProps = ButtonAnalyticsProps<OgMenuItemProps>;
export type SpeedDialActionProps = ButtonAnalyticsProps<OgSpeedDialActionProps>;
export type FabProps = ButtonAnalyticsProps<OgFabProps>;

/**
 * Fix for `component` prop overrides  from https://github.com/mui-org/material-ui/issues/15827#issuecomment-809209533
 * Used the "Use typecasting" fix
 * TODO if the above issue gets fixed we can get rid of this wonky typing
 */

export const Button = withButtonAnalytics(OgButton) as <
    C extends React.ElementType
>(
    props: ButtonWithOptionalAnalytics<OgButtonProps<C, { component?: C }>>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const CardActionArea = withButtonAnalytics(OgCardActionArea) as <
    C extends React.ElementType
>(
    props: ButtonWithOptionalAnalytics<
        OgCardActionAreaProps<C, { component?: C }>
    >,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const ButtonBase = withButtonAnalytics(OgButtonBase) as <
    C extends React.ElementType
>(
    props: ButtonWithOptionalAnalytics<OgButtonBaseProps<C, { component?: C }>>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const Chip = withButtonAnalytics(OgChip) as <
    C extends React.ElementType
>(
    props: ButtonWithOptionalAnalytics<OgChipProps<C, { component?: C }>>,
    ref?: React.Ref<HTMLDivElement>
) => React.ReactElement;

export const MenuItem = withButtonAnalytics(OgMenuItem) as <
    C extends React.ElementType
>(
    props: ButtonAnalyticsProps<OgMenuItemProps<C, { component?: C }>>,
    ref?: React.Ref<HTMLLIElement>
) => React.ReactElement;

export const ListItemButton = withButtonAnalytics(OgListItemButton) as <
    C extends React.ElementType
>(
    props: ButtonWithOptionalAnalytics<
        OgListItemButtonProps<C, { component?: C }>
    >,
    ref?: React.Ref<HTMLDivElement>
) => React.ReactElement;

export const IconButton = withButtonAnalytics(OgIconButton) as <
    C extends React.ElementType
>(
    props: ButtonWithOptionalAnalytics<OgIconButtonProps<C, { component?: C }>>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const BottomNavigationAction = withButtonAnalytics(
    OgBottomNavigationAction
) as <C extends React.ElementType>(
    props: ButtonWithOptionalAnalytics<
        OgBottomNavigationActionProps<C, { component?: C }>
    >,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const Tab = withButtonAnalytics(OgTab) as (
    props: ButtonWithOptionalAnalytics<TabProps>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const TableCellButton = withButtonAnalytics(TableCell) as (
    props: ButtonWithOptionalAnalytics<TableCellProps>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const SpeedDialAction = withButtonAnalytics(OgSpeedDialAction) as (
    props: ButtonWithOptionalAnalytics<OgSpeedDialActionProps>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const Fab = withButtonAnalytics(OgFab) as (
    props: ButtonWithOptionalAnalytics<OgFabProps>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const Dialog = withDialogAnalytics(OgDialog) as (
    props: DialogAnalyticsProps<OgDialogProps>,
    ref?: React.Ref<HTMLButtonElement>
) => React.ReactElement;

export const ButtonNoAnalytics = OgButton;
